import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
} from "recharts";
import { format, subMonths, subYears } from "date-fns";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import ChartCard from "./ChartCard";

// Updated options
const dateOptions = [
  { label: "3 Months", value: "3m" },
  { label: "6 Months", value: "6m" },
  { label: "1 Year", value: "1y" },
  { label: "2 Years", value: "2y" },
  { label: "3 Years", value: "3y" },
  { label: "5 Years", value: "5y" },
];

const formatYAxis = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value;
};

const InvoiceBarChartMonthly = ({
  title,
  color = "#4caf50",
  dataKey = "invoiceData",
}) => {
  const [data, setData] = useState([]);
  const [rangeType, setRangeType] = useState("3m");
  const [selectedLabel, setSelectedLabel] = useState("3 Months");
  const [loading, setLoading] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const chunkSize = 4;

  const { company_id } = useSelector((state) => state.GlobalVariables);

  const handleMenuOpen = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleRangeChange = (type) => {
    const selectedOption = dateOptions.find((opt) => opt.value === type);
    setRangeType(type);
    setSelectedLabel(selectedOption?.label || "Custom");
    setPage(0);
    handleMenuClose();
  };

  const getDateRange = () => {
    const today = new Date();
    let startDate;

    switch (rangeType) {
      case "3m":
        startDate = subMonths(today, 3);
        break;
      case "6m":
        startDate = subMonths(today, 6);
        break;
      case "1y":
        startDate = subYears(today, 1);
        break;
      case "2y":
        startDate = subYears(today, 2);
        break;
      case "3y":
        startDate = subYears(today, 3);
        break;
      case "5y":
        startDate = subYears(today, 5);
        break;
      default:
        startDate = null;
    }

    return {
      start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
      end_date: format(today, "yyyy-MM-dd"),
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { start_date, end_date } = getDateRange();
      const payload = {
        company_id,
        ...(start_date && end_date && { start_date, end_date }),
      };

      const result = await configServ.getMonthlyInvoiceCount(payload);
      if (result.status) {
        const raw = result[dataKey] || [];
        const parsed = raw.map((item) => ({
          month: item.label || item.date || item.month,
          value: parseFloat(item.invoice_count || item.total_invoices),
        }));
        setData(parsed);
        setPage(Math.max(Math.ceil(parsed.length / chunkSize) - 1, 0));
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [rangeType]);

  const totalPages = Math.ceil(data.length / chunkSize);
  const startIndex = page * chunkSize;
  const paginatedData = data.slice(startIndex, startIndex + chunkSize);

  return (
    <ChartCard
      title={
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">{title}</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {selectedLabel}
            </Typography>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>
      }
      loading={loading}
    >
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {dateOptions.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleRangeChange(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      {!loading && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={paginatedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip />
              <Bar dataKey="value" fill={color} />
            </BarChart>
          </ResponsiveContainer>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setPage(page + 1)}
              disabled={page + 1 >= totalPages}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </ChartCard>
  );
};

export default InvoiceBarChartMonthly;
